import { useMemo, useState, type InputHTMLAttributes } from 'react';
import type {
  BucketProfile,
  BucketProfileFact,
  BucketValueKind,
  SmartFillBucket
} from '@oysterjs/smartfill-contracts';
import {
  bestProfile,
  isCommonFact,
  profileCoverage,
  type ProfileRecord,
  type ProfileSection
} from '../buckets/profiles';
import { rememberBucketProfile, rememberedBucketProfile } from '../sidebar/profiles';
import { PlusIcon } from './Icons';
import { ProfilePicker, profileOptions } from './ProfilePicker';

/**
 * What carriers selling one product usually ask for, laid against the bucket:
 * which of those facts it holds and which it still needs, most-asked first.
 */
export function BucketProfilePattern({
  bucket,
  profiles,
  busy,
  onSetFact
}: {
  bucket: SmartFillBucket;
  profiles: BucketProfile[];
  busy: boolean;
  onSetFact: (fact: BucketProfileFact, value: string, entityId: string | null) => Promise<boolean>;
}) {
  const [choice, setChoice] = useState(() => rememberedBucketProfile(bucket.id));
  const profile =
    choice === null
      ? bestProfile(bucket.document, profiles)
      : (profiles.find((candidate) => candidate.id === choice) ?? null);

  const options = useMemo(() => profileOptions(profiles), [profiles]);

  const choose = (profileId: string) => {
    setChoice(profileId);
    rememberBucketProfile(bucket.id, profileId);
  };

  return (
    <section className="profile-pattern" aria-labelledby="profile-pattern-heading">
      <h2 id="profile-pattern-heading">What carriers ask for</h2>
      <ProfilePicker
        label="Profile"
        options={options}
        value={profile?.id ?? ''}
        placeholder="Choose a profile"
        onChange={choose}
      />
      {profile ? (
        <ProfileBody
          key={profile.id}
          bucket={bucket}
          profile={profile}
          busy={busy}
          onSetFact={onSetFact}
        />
      ) : (
        <p className="profile-pattern__hint">
          Choose a profile to see which facts its carriers ask for and which this bucket is missing.
        </p>
      )}
    </section>
  );
}

function ProfileBody({
  bucket,
  profile,
  busy,
  onSetFact
}: {
  bucket: SmartFillBucket;
  profile: BucketProfile;
  busy: boolean;
  onSetFact: (fact: BucketProfileFact, value: string, entityId: string | null) => Promise<boolean>;
}) {
  const common = profile.facts.filter((fact) => isCommonFact(profile, fact));
  const fewer = profile.facts.filter((fact) => !isCommonFact(profile, fact));
  const coverage = profileCoverage(bucket.document, common);
  const percent = coverage.factCount
    ? Math.round((coverage.heldCount / coverage.factCount) * 100)
    : 0;
  const askers =
    profile.carriers.length === 1 ? `${profile.carriers[0]} asks` : 'carriers commonly ask';
  const props = { profile, busy, canEdit: bucket.canEdit, onSetFact };

  return (
    <>
      <p className="profile-pattern__summary">
        Has {coverage.heldCount} of the {coverage.factCount} facts {askers} for
        {profile.carriers.length > 1 && ` · ${profile.carriers.length} carriers`}
      </p>
      <div
        className="profile-pattern__meter"
        role="meter"
        aria-label="Profile facts held"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      {coverage.sections.map((section, index) => (
        <PatternSection
          key={section.relationPath}
          section={section}
          open={index === 0}
          {...props}
        />
      ))}
      {fewer.length > 0 && (
        <details className="profile-pattern__fewer">
          <summary>Asked by fewer carriers ({fewer.length})</summary>
          {profileCoverage(bucket.document, fewer).sections.map((section) => (
            <PatternSection key={section.relationPath} section={section} open={false} {...props} />
          ))}
        </details>
      )}
    </>
  );
}

function PatternSection({
  section,
  open,
  profile,
  busy,
  canEdit,
  onSetFact
}: {
  section: ProfileSection;
  open: boolean;
  profile: BucketProfile;
  busy: boolean;
  canEdit: boolean;
  onSetFact: (fact: BucketProfileFact, value: string, entityId: string | null) => Promise<boolean>;
}) {
  return (
    <details className="profile-section" open={open}>
      <summary>
        <span>{section.title}</span>
        <span className="profile-section__count">
          {section.heldCount} of {section.facts.length}
        </span>
      </summary>
      {section.records.map((record, index) => (
        <PatternRecord
          key={record.entity?.id ?? `new-${index}`}
          record={record}
          section={section}
          showLabel={section.records.length > 1 || Boolean(record.summary)}
          profile={profile}
          busy={busy}
          canEdit={canEdit}
          onSetFact={onSetFact}
        />
      ))}
    </details>
  );
}

function PatternRecord({
  record,
  section,
  showLabel,
  profile,
  busy,
  canEdit,
  onSetFact
}: {
  record: ProfileRecord;
  section: ProfileSection;
  showLabel: boolean;
  profile: BucketProfile;
  busy: boolean;
  canEdit: boolean;
  onSetFact: (fact: BucketProfileFact, value: string, entityId: string | null) => Promise<boolean>;
}) {
  const [adding, setAdding] = useState<BucketProfileFact | null>(null);
  return (
    <div className="profile-record">
      {showLabel && (
        <p className="profile-record__label">
          {record.label}
          {record.summary && <span> · {record.summary}</span>}
        </p>
      )}
      {record.entity === null && section.relationPath !== '' && (
        <p className="profile-record__empty">No {section.recordName} yet.</p>
      )}
      <ul className="profile-facts">
        {record.missing.map((fact) => (
          <li key={fact.semanticKey} className="profile-fact">
            {adding === fact ? (
              <FactInput
                fact={fact}
                busy={busy}
                onCancel={() => setAdding(null)}
                onSave={async (value) => {
                  if (await onSetFact(fact, value, record.entity?.id ?? null)) setAdding(null);
                }}
              />
            ) : (
              <>
                <span className="profile-fact__label">{fact.label}</span>
                {profile.carriers.length > 1 && (
                  <span className="profile-fact__asked">
                    {fact.carrierCount} of {profile.carriers.length}
                  </span>
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="profile-fact__add"
                    disabled={busy || adding !== null}
                    aria-label={`Add ${fact.label}${showLabel ? ` to ${record.label}` : ''}`}
                    onClick={() => setAdding(fact)}
                  >
                    <PlusIcon />
                    Add
                  </button>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {record.held.length > 0 && (
        <p className="profile-record__held">
          Has {record.held.map((fact) => fact.label).join(', ')}
        </p>
      )}
    </div>
  );
}

function FactInput({
  fact,
  busy,
  onSave,
  onCancel
}: {
  fact: BucketProfileFact;
  busy: boolean;
  onSave: (value: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const [missing, setMissing] = useState(false);
  const id = `profile-fact-${fact.relationPath}-${fact.semanticKey}`;
  return (
    <form
      className="profile-fact__form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!value.trim()) {
          setMissing(true);
          return;
        }
        void onSave(value.trim());
      }}
    >
      <label htmlFor={id}>{fact.label}</label>
      <ValueInput
        id={id}
        kind={fact.kind}
        value={value}
        onChange={(next) => {
          setValue(next);
          setMissing(false);
        }}
      />
      {missing && <p className="profile-fact__error">Enter a value first.</p>}
      <div className="profile-fact__actions">
        <button
          type="button"
          className="button button--secondary button--pill"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className="button button--primary button--pill" disabled={busy}>
          Save
        </button>
      </div>
    </form>
  );
}

function ValueInput({
  id,
  kind,
  value,
  onChange
}: {
  id: string;
  kind: BucketValueKind;
  value: string;
  onChange: (value: string) => void;
}) {
  if (kind === 'BOOLEAN') {
    return (
      <select id={id} autoFocus value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Choose…</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  const typed: Partial<Record<BucketValueKind, InputHTMLAttributes<HTMLInputElement>>> = {
    DATE: { type: 'date' },
    EMAIL: { type: 'email' },
    PHONE: { type: 'tel' },
    NUMBER: { inputMode: 'decimal' },
    MONEY: { inputMode: 'decimal' }
  };
  return (
    <input
      id={id}
      autoFocus
      value={value}
      onChange={(event) => onChange(event.target.value)}
      {...typed[kind]}
    />
  );
}
